'use client';

import { useSignIn } from '@/hooks/useSignIn';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Alert, Box, Button, Container, IconButton, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import GoogleSignIn from '../GoogleSignIn';
import Loading from '../Loading';
type Props = {
  redirectTo: string;
  isInModal?: boolean;
  setModalView?: (view: 'signIn' | 'signUp') => void;
};
export default function SignInForm({
  redirectTo,
  isInModal = false,
  setModalView
}: Props) {
  const router = useRouter();
  const [form, setForm] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isRouting, setIsRouting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const mutation = useSignIn(() => {
    setIsRedirecting(true);
    router.push(redirectTo);
  }, err => {
    const msg = err?.response?.data?.error || 'Invalid email or password';
    setError(msg);
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    mutation.mutate(form);
  };
  const isLoading = mutation.isPending || isRedirecting;
  const handleClickShowPassword = () => {
    setShowPassword(prev => !prev);
  };
  const renderMainSignIn = () => {
    return <Box data-sentry-element="Box" data-sentry-component="renderMainSignIn" data-sentry-source-file="SignInForm.tsx">
        <GoogleSignIn context="signin" data-sentry-element="GoogleSignIn" data-sentry-source-file="SignInForm.tsx" />
        {isRouting || isLoading ? <Loading message="Signing in..." /> : null}
        <form onSubmit={handleSubmit}>
          <Stack spacing={3} data-sentry-element="Stack" data-sentry-source-file="SignInForm.tsx">
            <TextField label="Email" fullWidth value={form.email} onChange={e => setForm({
            ...form,
            email: e.target.value
          })} data-sentry-element="TextField" data-sentry-source-file="SignInForm.tsx" />

            <TextField label="Password" type={showPassword ? 'text' : 'password'} fullWidth value={form.password} onChange={e => setForm({
            ...form,
            password: e.target.value
          })} InputProps={{
            endAdornment: <InputAdornment position="end">
                    <IconButton onClick={handleClickShowPassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
          }} data-sentry-element="TextField" data-sentry-source-file="SignInForm.tsx" />

            <Button type="submit" variant="contained" color="primary" fullWidth size="large" disabled={isLoading} data-sentry-element="Button" data-sentry-source-file="SignInForm.tsx">
              {isLoading ? 'Signing in…' : 'Sign In'}
            </Button>

            {error && <Alert severity="error">{error}</Alert>}

            <Typography variant="body2" align="center" sx={{
            mt: 2
          }} data-sentry-element="Typography" data-sentry-source-file="SignInForm.tsx">
              Don't have an account?
              {' '}
            </Typography>
            {setModalView ? <Button size="small" variant="outlined" loading={isRouting} sx={{
            fontWeight: 600,
            textTransform: 'none'
          }} onClick={() => {
            setModalView('signUp');
            setIsRouting(false);
          }}>
                    Sign Up
                  </Button> : <Button href="/sign-up" component="a" variant="outlined" size="small" loading={isRouting} onClick={() => setIsRouting(true)} sx={{
            fontWeight: 600,
            textTransform: 'none'
          }}>
                    Sign Up
                  </Button>}

          </Stack>
        </form>
      </Box>;
  };
  if (isInModal) {
    return <Container maxWidth="xs" sx={{
      py: 0
    }}>
        {renderMainSignIn()}
      </Container>;
  }
  return <Container maxWidth="xs" sx={{
    py: 8
  }} data-sentry-element="Container" data-sentry-component="SignInForm" data-sentry-source-file="SignInForm.tsx">
      <Paper elevation={3} sx={{
      p: 4,
      borderRadius: 3
    }} data-sentry-element="Paper" data-sentry-source-file="SignInForm.tsx">
        <Typography variant="h5" fontWeight={700} gutterBottom align="center" data-sentry-element="Typography" data-sentry-source-file="SignInForm.tsx">
          Sign In
        </Typography>
        {renderMainSignIn()}
      </Paper>
    </Container>;
}